import { Alert, Button, Col, Form, Input, Modal, notification, Row, Select } from "antd";
import api from "ApiClient";
import { ApiURL } from "utils/Actions";
import { useState } from "react";
import { useAppSelector } from "redux/hooks";
import { ROLE_OPTION, schoolContactLabel } from "utils/AssetsData";
import { API_RESPONSE_STATUS, CONTACT_FIELD, CREDENTIAL_MODAL_STATUS } from "utils/Constants";
import { CredentialModalStatusType, RoleType, SchoolContactTableType } from "utils/Types";
import { alphabeticalRule } from "utils/validators/rules";
import { principal, common } from "utils/Wording";
import { fetchRaters } from "redux/reducers/CredentialsSlice";
import { useAppDispatch } from "redux/hooks";
import { useTranslation } from "react-i18next";
import { showSuccessNotification } from "utils/hooks/handleNotification";
import { capitalize } from "lodash";
import { allocateOptions } from "utils/hooks/allocateOptions";

const { Option } = Select;

const transformToUpperCaseFields = [
  CONTACT_FIELD.FIRST_NAME,
  CONTACT_FIELD.LAST_NAME,
  CONTACT_FIELD.POSITION
];

type InviteStaffModalType = {
  isAddModalOpen: boolean;
  modalStatus: CredentialModalStatusType;
  onClose: () => void;
  changeModalToView: () => void;
};

const InviteStaffModal: React.FC<InviteStaffModalType> = ({
  isAddModalOpen,
  modalStatus,
  onClose,
  changeModalToView
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const errorText = common.failAsync;
  const [raterData, setRaterData] = useState<SchoolContactTableType | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [asyncError, setAsyncError] = useState<string[]>([]);
  const { whiteLabel = "" } = useAppSelector(state => state.properties);
  const roleSelectOption = ROLE_OPTION[whiteLabel];

  const handleFailingOnSave = () => {
    notification["error"]({
      message: errorText
    });
  };

  const onAlertClose = () => {
    setAsyncError([]);
  };

  const onCloseAndFinish = () => {
    onClose();
    form.resetFields();
    setAsyncError([]);
    setRaterData(undefined);
  };

  const handleInviteStaffSubmit = () => {
    form
      .validateFields()
      .then(async values => {
        setSubmitting(true);
        // must transform relevant fields to uppercase here;
        // normalize() and onInput() cause bugs
        const formattedValues: any = {};
        Object.keys(values).forEach(key => {
          if (transformToUpperCaseFields.includes(key)) {
            if (values[key]) {
              formattedValues[key] = values[key].toUpperCase();
            }
          } else {
            formattedValues[key] = values[key];
          }
        });
        try {
          const res = await api.put(ApiURL.inviteStaff, formattedValues);
          const { status, errors } = res.data;
          if (status === API_RESPONSE_STATUS.SUCCESS) {
            changeModalToView();
            setRaterData(values);
            dispatch(fetchRaters());
            onCloseAndFinish();
            showSuccessNotification({
              message: t("inviteStaff.successMsg", {
                name: `${capitalize(values.firstName)} ${capitalize(values.lastName)}`
              })
            });
          } else {
            setAsyncError(errors);
            return false;
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error.response && error.response.data.errors;
          setAsyncError(errorMessage);
          handleFailingOnSave();
          return false;
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Modal
      title={t("inviteStaff.heading")}
      open={isAddModalOpen}
      closable={!submitting}
      maskClosable={false}
      onCancel={onCloseAndFinish}
      style={{ width: 572 }}
      footer={
        modalStatus === CREDENTIAL_MODAL_STATUS.ADD
          ? [
              <Button key="cancel" onClick={onCloseAndFinish} disabled={submitting}>
                {t("inviteStaff.cancelCTA")}
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleInviteStaffSubmit}
                disabled={submitting}
                loading={submitting}
              >
                {t("inviteStaff.inviteCTA")}
              </Button>
            ]
          : [
              <Button key="close" type="primary" onClick={onCloseAndFinish}>
                {t("inviteStaff.closeCTA")}
              </Button>
            ]
      }
    >
      {modalStatus === CREDENTIAL_MODAL_STATUS.ADD && (
        <>
          <p>{t("inviteStaff.description")}</p>
          <Form
            disabled={submitting}
            form={form}
            onFinish={handleInviteStaffSubmit}
            layout="vertical"
            requiredMark={false}
          >
            {asyncError?.length > 0 &&
              asyncError.map(err => (
                <Alert
                  key={err}
                  type="error"
                  showIcon={true}
                  message={err}
                  closable
                  onClose={onAlertClose}
                  className="gap-section"
                />
              ))}

            <Form.Item
              required
              label={schoolContactLabel.username}
              name={CONTACT_FIELD.EMAIL}
              validateTrigger="onBlur"
              rules={[
                {
                  required: false,
                  type: "email",
                  message: "Not a valid email"
                },
                {
                  required: true,
                  message: "Email is required"
                }
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item
              required
              label={schoolContactLabel.role}
              name={CONTACT_FIELD.ROLES}
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: "Role is required"
                }
              ]}
            >
              <Select mode="multiple" getPopupContainer={allocateOptions}>
                {roleSelectOption &&
                  roleSelectOption.map((e: RoleType) => (
                    <Option key={e.value} value={e.value}>
                      {t(e.labelKey)}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item
              required
              label={schoolContactLabel.firstName}
              name={CONTACT_FIELD.FIRST_NAME}
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: "First name is required"
                },
                alphabeticalRule
              ]}
            >
              <Input maxLength={15} className="display-as-uppercase" />
            </Form.Item>
            <Form.Item
              required
              label={schoolContactLabel.lastName}
              name={CONTACT_FIELD.LAST_NAME}
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: "Last name is required"
                },
                alphabeticalRule
              ]}
            >
              <Input maxLength={20} className="display-as-uppercase" />
            </Form.Item>
            <Form.Item
              label={schoolContactLabel.position}
              name={CONTACT_FIELD.POSITION}
              rules={[alphabeticalRule]}
            >
              <Input maxLength={30} className="display-as-uppercase" />
            </Form.Item>
          </Form>
        </>
      )}
      {modalStatus === CREDENTIAL_MODAL_STATUS.VIEW && raterData && (
        <>
          <p>
            {principal.credential.addModal.displayDesc}
            <br />
            <strong>{raterData.email}</strong>
          </p>
          <Row>
            <Col span={8}>
              <div style={{ textAlign: "right" }}>
                <p>{schoolContactLabel.username}: &nbsp;</p>
                <p>{schoolContactLabel.firstName}: &nbsp;</p>
                <p>{schoolContactLabel.lastName}: &nbsp;</p>
                <p>{schoolContactLabel.position}: &nbsp;</p>
              </div>
            </Col>
            <Col span={16}>
              <div>
                <p>{raterData.email}</p>
                <p>{raterData.givenName1}</p>
                <p>{raterData.familyName}</p>
                <p>{raterData.position}&nbsp;</p>
              </div>
            </Col>
          </Row>
          <p>{principal.credential.addModal.displayInfo}</p>
        </>
      )}
    </Modal>
  );
};

export default InviteStaffModal;
